import {
  type ShoppingListItem,
  shoppingListService,
} from "../services/shoppingListService";
import { useMutation, useQuery, useQueryClient } from "./useDataCache";
import { useUser } from "./useUser";

// Hook to load shopping list and keep it in sync via realtime updates and optimistic mutations
export function useShoppingList() {
  const queryClient = useQueryClient();
  const { householdId, userId } = useUser();

  // Load shopping list items for the current household
  const { data: items = [], ...queryInfo } = useQuery({
    queryKey: ["shoppingList", householdId],
    queryFn: () => {
      if (!householdId) return Promise.resolve([]);
      return shoppingListService.getItems(householdId);
    },
    enabled: !!householdId,
  });

  // Mutations with optimistic updates: UI updates instantly, rolls back on error,
  // and re-syncs from the server once the mutation settles.
  const addItemMutation = useMutation({
    mutationFn: ({
      name,
      quantity,
      unit,
      category,
    }: {
      name: string;
      quantity: number | null;
      unit: string | null;
      category: string | null;
    }) => {
      if (!householdId || !userId) throw new Error("Not authenticated");
      return shoppingListService.addItem(
        name,
        quantity,
        unit,
        category,
        householdId,
        userId,
      );
    },
    onMutate: async ({ name, quantity, unit, category }) => {
      await queryClient.cancelQueries({
        queryKey: ["shoppingList", householdId],
      });
      const previous = queryClient.getQueryData<ShoppingListItem[]>([
        "shoppingList",
        householdId,
      ]);

      const optimisticItem: ShoppingListItem = {
        id: `optimistic-${Date.now()}`,
        user_id: userId ?? "",
        household_id: householdId ?? "",
        item_name: name,
        quantity,
        unit,
        category: category ?? undefined,
        purchased: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueryData<ShoppingListItem[]>(
        ["shoppingList", householdId],
        (old) => (old ? [optimisticItem, ...old] : [optimisticItem]),
      );

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(
          ["shoppingList", householdId],
          context.previous,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["shoppingList", householdId],
      });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id: string) => shoppingListService.deleteItem(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({
        queryKey: ["shoppingList", householdId],
      });
      const previous = queryClient.getQueryData<ShoppingListItem[]>([
        "shoppingList",
        householdId,
      ]);
      queryClient.setQueryData<ShoppingListItem[]>(
        ["shoppingList", householdId],
        (old) => (old ? old.filter((item) => item.id !== id) : []),
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(
          ["shoppingList", householdId],
          context.previous,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["shoppingList", householdId],
      });
    },
  });

  const deleteItemsMutation = useMutation({
    mutationFn: (ids: string[]) => shoppingListService.deleteItems(ids),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({
        queryKey: ["shoppingList", householdId],
      });
      const previous = queryClient.getQueryData<ShoppingListItem[]>([
        "shoppingList",
        householdId,
      ]);
      queryClient.setQueryData<ShoppingListItem[]>(
        ["shoppingList", householdId],
        (old) => (old ? old.filter((item) => !ids.includes(item.id)) : []),
      );
      return { previous };
    },
    onError: (_err, _ids, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(
          ["shoppingList", householdId],
          context.previous,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["shoppingList", householdId],
      });
    },
  });

  const togglePurchasedMutation = useMutation({
    mutationFn: (item: ShoppingListItem) =>
      item.purchased
        ? shoppingListService.markAsUnpurchased(item.id)
        : shoppingListService.markAsPurchased(item.id),
    onMutate: async (item) => {
      await queryClient.cancelQueries({
        queryKey: ["shoppingList", householdId],
      });
      const previous = queryClient.getQueryData<ShoppingListItem[]>([
        "shoppingList",
        householdId,
      ]);
      queryClient.setQueryData<ShoppingListItem[]>(
        ["shoppingList", householdId],
        (old) =>
          old
            ? old.map((i) =>
                i.id === item.id ? { ...i, purchased: !item.purchased } : i,
              )
            : [],
      );
      return { previous };
    },
    onError: (_err, _item, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(
          ["shoppingList", householdId],
          context.previous,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["shoppingList", householdId],
      });
    },
  });

  return {
    items,
    ...queryInfo,
    addItem: addItemMutation.mutateAsync,
    deleteItem: deleteItemMutation.mutateAsync,
    deleteItems: deleteItemsMutation.mutateAsync,
    togglePurchased: togglePurchasedMutation.mutateAsync,
  };
}
